import { useState, useRef, useEffect } from "react";
import { Form, Button, Alert, Col, Row } from "react-bootstrap";
import ModalLayout from "components/modal/modal-layout/ModalLayout";
import CustomSelectionControl from "components/custom-selection-control/CustomSelectionControl";
import CustomSelect from "components/custom-select/CustomSelect";
import { ReactComponent as OutlineShiedCross } from "assets/svg/outline-shield-cross.svg";
import { AmplitudePageName, track } from "utils/amplitude";
import {
  //   VIDEO_LINK_OPTIONS,
  TELEHEALTH_POS_OPTIONS,
  TELEHEALTH_MODIFIER_OPTIONS,
  MODIFIER_OPTIONS,
  IN_PERSON_POS_OPTIONS,
} from "./constants";
import { STATE_OPTIONS } from "constants/stateOptions";
import postOrchidPro from "services/postOrchidPro";
import { PRACTICE_SETTING } from "components/provider-intake/utility/providerInfo";
import { filter_keys } from "utils/fn";
import { OfficeLocation } from "./PracticeDetails";
import messages from "messages";
import styles from "./OfficeLocationEditor.module.scss";

const AMPLITUDE_COMPONENT_NAME = "location-editor-modal";

const OfficeLocationEditor = ({
  show,
  onHide,
  amplitudePageName,
  locationItem,
  editMode,
  setData,
  additionalLocations,
}: {
  show: boolean;
  onHide: () => void;
  amplitudePageName: AmplitudePageName;
  locationItem?: any;
  editMode: boolean;
  setData: (data: Partial<OrchidPro>) => void;
  additionalLocations: OfficeLocation[];
}) => {
  const {
    office_name: itemOfficeName,
    // video_link: itemVideoLink,
    session_location_type: itemSessionLocationType,
    office_address_line1: itemAddressLine1,
    office_address_line2: itemAddressLine2,
    office_city: itemCity,
    office_state: itemState,
    office_zipcode: itemZipCode,
    phone_number: itemPhoneNumber,
    email_address: itemEmailAddress,
    telehealth_pos_code: itemTelehealthPosCode,
    telehealth_modifier: itemTelehealthModifier,
    in_person_pos_code: itemInPersonPosCode,
    in_person_modifier: itemInPersonModifier,
    show_address_to_public: itemShowAddress,
    is_default: itemIsDefault,
    office_location_id: itemOfficeLocationId,
  } = locationItem || {};

  const isTelehealth = itemSessionLocationType === "telehealth";

  const [officeName, setOfficeName] = useState(itemOfficeName || "");
  //   const [videoLink, setVideoLink] = useState(itemVideoLink || "");
  const [addressLine1, setAddressLine1] = useState(itemAddressLine1 || "");
  const [addressLine2, setAddressLine2] = useState(itemAddressLine2 || "");
  const [city, setCity] = useState(itemCity || "");
  const [state, setState] = useState(itemState || "");
  const [zipCode, setZipCode] = useState(itemZipCode || "");
  const [phoneNumber, setPhoneNumber] = useState(itemPhoneNumber || "");
  const [emailAddress, setEmailAddress] = useState(itemEmailAddress || "");
  const [posCode, setPosCode] = useState(
    isTelehealth ? itemTelehealthPosCode : itemInPersonPosCode,
  );
  const [modifier, setModifier] = useState(
    isTelehealth ? itemTelehealthModifier : itemInPersonModifier,
  );
  const [showAddress, setShowAddress] = useState(itemShowAddress || false);
  const [validated, setValidated] = useState(false);
  const [error, setError] = useState<any>();

  const errorRef = useRef<HTMLDivElement>(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);

    const form = e.currentTarget;
    if (!form.checkValidity()) {
      setError("Please fill out all required fields correctly.");
      return;
    }

    if (itemOfficeLocationId == null) {
      setError("Please select an office location to update.");
      return;
    }

    const editedLocation = {
      office_name: officeName,
      session_location_type: itemSessionLocationType,
      office_address_line1: addressLine1,
      office_address_line2: addressLine2,
      office_city: city,
      office_state: state,
      office_zipcode: zipCode,
      show_address_to_public: showAddress,
      is_default: itemIsDefault,
      office_location_id: itemOfficeLocationId,
      ...(isTelehealth
        ? {
            telehealth_pos_code: posCode,
            telehealth_modifier: modifier,
          }
        : {
            in_person_pos_code: posCode,
            in_person_modifier: modifier ? modifier : null,
          }),
    };

    try {
      let nextAdditionalLocations: OfficeLocation[] = [...additionalLocations];

      nextAdditionalLocations = nextAdditionalLocations.map((loc) =>
        loc.office_location_id === itemOfficeLocationId ? editedLocation : loc,
      );

      const nextOrchidPro = await postOrchidPro({
        additional_office_locations: nextAdditionalLocations,
        ...(isTelehealth
          ? {
              telehealth_pos_code: posCode,
              telehealth_modifier: modifier,
            }
          : {
              in_person_pos_code: posCode,
              in_person_modifier: modifier ? modifier : null,
            }),
      });

      setData(filter_keys(nextOrchidPro, PRACTICE_SETTING));

      onHide();
    } catch (error) {
      setError(error);
    }
  };

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [error]);

  return (
    <ModalLayout
      showHeaderDivider
      size="md"
      show={show}
      onHide={onHide}
      Header={
        <div className={styles["location-editor__header-title"]}>
          {editMode ? "Edit Office Location" : "Add New Office Location"}
        </div>
      }
      bodyClass={styles["location-editor__body"]}
      footerClass={styles["location-editor__footer"]}
      Footer={
        <>
          <Button variant="outline-primary" className="mr-2" onClick={onHide}>
            {messages.cancel}
          </Button>
          <Button form="location-editor-form" variant="primary" type="submit">
            {editMode ? messages.update : messages.add}
          </Button>
        </>
      }
    >
      <Form
        noValidate
        validated={validated}
        onSubmit={handleSubmit}
        id="location-editor-form"
      >
        {error && (
          <Alert variant="danger" ref={errorRef}>
            {error}
          </Alert>
        )}

        {/* Session Location */}
        <div className={styles["location-editor__section-title"]}>
          {isTelehealth
            ? "Telehealth Session Location"
            : "In-person Session Location"}{" "}
          <OutlineShiedCross />
        </div>

        {/* Office Name */}
        <Form.Group>
          <Form.Label className={styles["location-editor__form-label"]}>
            Office Name
          </Form.Label>
          <Form.Control
            disabled={isTelehealth}
            type="text"
            placeholder="Enter Office Name"
            value={officeName}
            onChange={(e) => setOfficeName(e.target.value)}
            required
          />
        </Form.Group>

        {/* Video Link */}
        {/* <Form.Group>
          <Form.Label className={styles["location-editor__form-label"]}>
            Video Link <span className="required">*</span>
          </Form.Label>
          <CustomSelect
            options={VIDEO_LINK_OPTIONS}
            value={VIDEO_LINK_OPTIONS.find(
              (option) => option.value === videoLink,
            )}
            onChange={(option) => setVideoLink(option.value)}
            placeholder="Select Video Link"
          />
        </Form.Group> */}

        {/* Address Fields */}
        <Form.Group>
          <Form.Label className={styles["location-editor__form-label"]}>
            Street <span className="required">*</span>
          </Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter Street"
            value={addressLine1}
            onChange={(e) => setAddressLine1(e.target.value)}
            required
          />
        </Form.Group>

        <Form.Group>
          <Form.Label className={styles["location-editor__form-label"]}>
            Address 2 (Optional)
          </Form.Label>
          <Form.Control
            type="text"
            placeholder="Apt, Suite, Floor, etc."
            value={addressLine2}
            onChange={(e) => setAddressLine2(e.target.value)}
          />
        </Form.Group>

        <Row>
          <Form.Group as={Col} md={4}>
            <Form.Label className={styles["location-editor__form-label"]}>
              City <span className="required">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group as={Col} md={4}>
            <Form.Label className={styles["location-editor__form-label"]}>
              State <span className="required">*</span>
            </Form.Label>
            <CustomSelect
              id={"office_state"}
              name={"office_state"}
              options={STATE_OPTIONS}
              onChange={(el) => setState(el.value)}
              value={STATE_OPTIONS.find((option) => option.value === state)}
              placeholder={messages.select}
              onClick={() =>
                track(
                  amplitudePageName,
                  [AMPLITUDE_COMPONENT_NAME, "state-dropdown"],
                  "click",
                )
              }
            />
          </Form.Group>

          <Form.Group as={Col} md={4}>
            <Form.Label className={styles["location-editor__form-label"]}>
              Zip Code <span className="required">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Zip Code"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              required
            />
          </Form.Group>
        </Row>

        {/* Phone and Email */}
        <Form.Group>
          <Form.Label className={styles["location-editor__form-label"]}>
            Phone Number
          </Form.Label>
          <Form.Control
            disabled
            type="text"
            placeholder="Enter Phone Number"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
        </Form.Group>

        <Form.Group>
          <Form.Label className={styles["location-editor__form-label"]}>
            Email Address
          </Form.Label>
          <Form.Control
            disabled
            type="email"
            placeholder="Enter Email Address"
            value={emailAddress}
            onChange={(e) => setEmailAddress(e.target.value)}
          />
        </Form.Group>

        {/* Insurance Place of Service */}
        <Form.Group>
          <Form.Label className={styles["location-editor__form-label"]}>
            Insurance Place of Service
          </Form.Label>
          {isTelehealth ? (
            <CustomSelect
              options={TELEHEALTH_POS_OPTIONS}
              value={TELEHEALTH_POS_OPTIONS.find(
                (option) => option.value === posCode,
              )}
              onChange={(option) => setPosCode(option.value)}
              placeholder="Select Place of Service"
            />
          ) : (
            <CustomSelect
              options={IN_PERSON_POS_OPTIONS}
              value={IN_PERSON_POS_OPTIONS.find(
                (option) => option.value === posCode,
              )}
              onChange={(option) => setPosCode(option.value)}
              placeholder="Select Place of Service"
            />
          )}
        </Form.Group>

        {/* Modifier */}
        {isTelehealth ? (
          <Form.Group>
            <Form.Label className={styles["location-editor__form-label"]}>
              Telehealth Modifier
            </Form.Label>
            <CustomSelect
              options={TELEHEALTH_MODIFIER_OPTIONS}
              value={TELEHEALTH_MODIFIER_OPTIONS.find(
                (option) => option.value === modifier,
              )}
              onChange={(option) => setModifier(option.value)}
              placeholder="Select Modifier"
            />
          </Form.Group>
        ) : (
          <Form.Group>
            <Form.Label className={styles["location-editor__form-label"]}>
              In-person Modifier
            </Form.Label>
            <CustomSelect
              options={MODIFIER_OPTIONS}
              value={MODIFIER_OPTIONS.find(
                (option) => option.value === modifier,
              )}
              onChange={(option) => setModifier(option.value)}
              placeholder="Select Modifier"
            />
          </Form.Group>
        )}
        <hr />
        <div className={styles["location-editor__section-title"]}>
          Public View
        </div>

        <div className={styles["location-editor__text"]}>
          Choose if you'd like your address to be visible to the general public.
          This setting will affect your directory profile, professional website,
          and Client Portals.
        </div>
        <Form.Group className={styles["location-editor__location-options"]}>
          <div className={styles["location-editor__location-option"]}>
            <CustomSelectionControl
              type="radio"
              label="Hide Address"
              checked={!showAddress}
              onChange={() => setShowAddress(false)}
            />
            Hide Address
          </div>
          <div className={styles["location-editor__location-option"]}>
            <CustomSelectionControl
              type="radio"
              label="Show Address"
              checked={showAddress}
              onChange={() => setShowAddress(true)}
            />
            Show Address
          </div>
        </Form.Group>
        {showAddress && (
          <div className={styles["location-editor__address-preview"]}>
            <div className={styles["location-editor__address-preview-title"]}>
              {officeName || "{Office Name}"}
            </div>
            <div className={styles["location-editor__address-preview-text"]}>
              {addressLine1 || ""}
              {addressLine2 ? `, ${addressLine2}` : ""}
              <br />
              {city}, {state} {zipCode}
            </div>
          </div>
        )}
      </Form>
    </ModalLayout>
  );
};

export default OfficeLocationEditor;
